import React, { useState, useMemo } from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Building2, Calendar as CalendarIcon, Search, Users } from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import AccommodationCard from "./AccommodationCard";

/**
 * @param {{
 *   hotels: Array<any>;
 *   onSelectHotel: (hotel: any) => void;
 *   prefillCheckin?: Date | null;
 *   prefillCheckout?: Date | null;
 *   prefillGuests?: number;
 * }} props
 */
export default function AccommodationSearchView({
  hotels,
  onSelectHotel,
  prefillCheckin,
  prefillCheckout,
  prefillGuests,
}) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const PopoverContentComp = /** @type {React.ComponentType<any>} */ (PopoverContent);

  const [searchQuery, setSearchQuery] = useState("");
  const [checkin, setCheckin] = useState(prefillCheckin || null);
  const [checkout, setCheckout] = useState(prefillCheckout || null);
  const [guests, setGuests] = useState(prefillGuests || 2);

  const filteredHotels = useMemo(() => {
    if (!searchQuery.trim()) return hotels;
    const q = searchQuery.toLowerCase();
    return hotels.filter(
      (h) =>
        h.name.toLowerCase().includes(q) ||
        h.area.toLowerCase().includes(q)
    );
  }, [hotels, searchQuery]);

  return (
    <div className="w-full bg-white">
      {/* Hero — masthead style */}
      <div className="relative">
        <div className="relative h-72 sm:h-80 bg-[#0B2421] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B2421]/40 to-[#0B2421]/80" />
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative h-full flex flex-col items-center justify-center text-center px-4 pb-16"
          >
            <Building2 className="h-8 w-8 text-[#D4AF37] mb-2" />
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-2">Accommodation</p>
            <h1 className="text-white font-bold text-3xl sm:text-4xl mb-1">Find Your Stay</h1>
            <p className="text-white/60 text-sm">Comfortable hotels near golf courses in Batam</p>
          </motion.div>
        </div>

        {/* Search form overlapping the banner */}
        <div className="absolute bottom-0 left-0 right-0 z-30 translate-y-1/2 px-3 sm:px-4">
          <div className="max-w-4xl mx-auto rounded-2xl shadow-2xl border border-white/50 p-3 bg-white/60 backdrop-blur-2xl">
            {/* Row 1: Search name | Check-in | Check-out | Guests */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              {/* Search */}
              <div className="flex items-center gap-2 bg-white/70 rounded-xl px-4 py-3 border border-white/60">
                <Search className="h-4 w-4 text-[#0B2421] shrink-0" />
                <input type="text" placeholder="Name or area..."
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="text-sm flex-1 bg-transparent outline-none text-gray-700 placeholder:text-gray-400" />
              </div>

              {/* Check-in */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="w-full flex items-center gap-2 bg-white/70 rounded-xl px-4 py-3 border border-white/60 text-left">
                    <CalendarIcon className="h-4 w-4 text-[#0B2421] shrink-0" />
                    <span className={`text-sm flex-1 truncate ${checkin ? "text-gray-700" : "text-gray-400"}`}>
                      {checkin ? format(checkin, "dd MMM yyyy") : "Check-in"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContentComp className="w-auto p-0" align="start">
                  <Calendar className="" classNames={{}} mode="single" selected={checkin} onSelect={setCheckin}
                    disabled={/** @param {Date} d */ (d) => d < new Date()} />
                </PopoverContentComp>
              </Popover>

              {/* Check-out */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="w-full flex items-center gap-2 bg-white/70 rounded-xl px-4 py-3 border border-white/60 text-left">
                    <CalendarIcon className="h-4 w-4 text-[#0B2421] shrink-0" />
                    <span className={`text-sm flex-1 truncate ${checkout ? "text-gray-700" : "text-gray-400"}`}>
                      {checkout ? format(checkout, "dd MMM yyyy") : "Check-out"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContentComp className="w-auto p-0" align="start">
                  <Calendar className="" classNames={{}} mode="single" selected={checkout} onSelect={setCheckout}
                    disabled={/** @param {Date} d */ (d) => !checkin || d <= checkin || d < new Date()} />
                </PopoverContentComp>
              </Popover>
            </div>

            {/* Row 2: Guests counter + Search button */}
            <div className="grid grid-cols-[1fr_auto] gap-3">
              <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3 border border-white/60">
                <Users className="h-4 w-4 text-[#0B2421] shrink-0" />
                <span className="text-sm text-gray-500">Guests</span>
                <div className="flex items-center gap-2 ml-auto">
                  <button onClick={() => setGuests(Math.max(1, guests - 1))}
                    className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-700 font-bold">−</button>
                  <span className="font-bold text-[#0B2421] w-5 text-center">{guests}</span>
                  <button onClick={() => setGuests(Math.min(8, guests + 1))}
                    className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-700 font-bold">+</button>
                </div>
              </div>
              <button className="flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] font-bold rounded-xl px-8 py-3 transition-colors whitespace-nowrap">
                <Search className="h-4 w-4" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer + Cards */}
      <div className="pt-44 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-[#0B2421]">
              {filteredHotels.length} {filteredHotels.length === 1 ? "Property" : "Properties"} Available
            </h2>
            <p className="text-gray-400 text-sm mt-1">Comfortable stays near golf courses and attractions</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredHotels.map((hotel, index) => (
              <AccommodationCard key={hotel.id} hotel={hotel} onSelect={() => onSelectHotel(hotel)} index={index} />
            ))}
          </div>

          {filteredHotels.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg">No properties found matching your search.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
